import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import LEGACY_REDIRECTS from './data/redirects.json';
import { PENDING_REDIRECTS } from './data/siteStructure';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import WhatsAppButton from './components/WhatsAppButton';

// CRM Components
import { AuthProvider } from './crm/context/AuthContext';
const ProtectedRoute = lazy(() => import('./crm/components/ProtectedRoute'));
const AuthCallback = lazy(() => import('./crm/components/AuthCallback'));
const CRMLayout = lazy(() => import('./crm/components/CRMLayout'));
const DataPage = lazy(() => import('./components/DataPage'));

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const ServiziPage = lazy(() => import('./pages/ServiziPage'));
const ChiSiamoPage = lazy(() => import('./pages/ChiSiamoPage'));
const ComeFunzionaPage = lazy(() => import('./pages/ComeFunzionaPage'));
const ContattiPage = lazy(() => import('./pages/ContattiPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ServiziAziendePage = lazy(() => import('./pages/ServiziAziendePage'));

// Service Pages
const LavaggioDivaniBologna = lazy(() => import('./pages/LavaggioDivaniBologna'));
const LavaggioMaterassiBologna = lazy(() => import('./pages/LavaggioMaterassiBologna'));
const LavaggioTappetiBologna = lazy(() => import('./pages/LavaggioTappetiBologna'));
const TecnologiaFortador = lazy(() => import('./pages/TecnologiaFortador'));
const PuliziaMoquetteBologna = lazy(() => import('./pages/PuliziaMoquetteBologna'));
const PuliziaInterniAutoBologna = lazy(() => import('./pages/PuliziaInterniAutoBologna'));
const PuliziaCamperBologna = lazy(() => import('./pages/PuliziaCamperBologna'));
const PuliziaBusFlotteBologna = lazy(() => import('./pages/PuliziaBusFlotteBologna'));
const LavaggioFinestreTapparelleBologna = lazy(() => import('./pages/LavaggioFinestreTapparelleBologna'));
const LavaggioFinestreAziendeBologna = lazy(() => import('./pages/LavaggioFinestreAziendeBologna'));

// Ferrara Service Pages
const LavaggioDivaniFerrara = lazy(() => import('./pages/LavaggioDivaniFerrara'));
const LavaggioMaterassiFerrara = lazy(() => import('./pages/LavaggioMaterassiFerrara'));
const LavaggioTappetiFerrara = lazy(() => import('./pages/LavaggioTappetiFerrara'));
const PuliziaInterniAutoFerrara = lazy(() => import('./pages/PuliziaInterniAutoFerrara'));

// Modena Service Pages
const LavaggioDivaniModena = lazy(() => import('./pages/LavaggioDivaniModena'));
const LavaggioMaterassiModena = lazy(() => import('./pages/LavaggioMaterassiModena'));
const LavaggioTappetiModena = lazy(() => import('./pages/LavaggioTappetiModena'));
const PuliziaInterniAutoModena = lazy(() => import('./pages/PuliziaInterniAutoModena'));

// Business Service Pages
const FlotteAziendaliVapore = lazy(() => import('./pages/FlotteAziendaliVapore'));
const AutobusTrasportoVapore = lazy(() => import('./pages/AutobusTrasportoVapore'));
const HotelBBPuliziaVapore = lazy(() => import('./pages/HotelBBPuliziaVapore'));
const RistorantiLocaliVapore = lazy(() => import('./pages/RistorantiLocaliVapore'));

// New Service Pages
const PuliziaCucineBologna = lazy(() => import('./pages/PuliziaCucineBologna'));
const PuliziaTendeSoleBologna = lazy(() => import('./pages/PuliziaTendeSoleBologna'));
const PuliziaTendeInterneBologna = lazy(() => import('./pages/PuliziaTendeInterneBologna'));
const LavaggioAutoVaporeBologna = lazy(() => import('./pages/LavaggioAutoVaporeBologna'));

// Blog Pages
const BlogPulireFinestreSenzaAloni = lazy(() => import('./pages/BlogPulireFinestreSenzaAloni'));
const BlogPuliziaCucinaVapore = lazy(() => import('./pages/BlogPuliziaCucinaVapore'));
const BlogPuliziaTendeSole = lazy(() => import('./pages/BlogPuliziaTendeSole'));
const BlogVaporeVsTradizionale = lazy(() => import('./pages/BlogVaporeVsTradizionale'));
const BlogPuliziaDivaniOgniQuanto = lazy(() => import('./pages/BlogPuliziaDivaniOgniQuanto'));
const BlogAcariMaterasso = lazy(() => import('./pages/BlogAcariMaterasso'));
const BlogVaporeEcologico = lazy(() => import('./pages/BlogVaporeEcologico'));
const BlogPuliziaInterniAuto = lazy(() => import('./pages/BlogPuliziaInterniAuto'));
const BlogTappetiComeRipulirli = lazy(() => import('./pages/BlogTappetiComeRipulirli'));

// New Blog Articles (2026-04-27)
const BlogCostoPuliziaDivano = lazy(() => import('./pages/BlogCostoPuliziaDivano'));
const BlogPuliziaMaterassoSonno = lazy(() => import('./pages/BlogPuliziaMaterassoSonno'));
const BlogTappetiSporchiOdori = lazy(() => import('./pages/BlogTappetiSporchiOdori'));
const BlogAutoPeliCane = lazy(() => import('./pages/BlogAutoPeliCane'));
const BlogMacchieDifficili = lazy(() => import('./pages/BlogMacchieDifficili'));
const BlogVaporeCaseBambini = lazy(() => import('./pages/BlogVaporeCaseBambini'));
const BlogOdoreCaneDivano = lazy(() => import('./pages/BlogOdoreCaneDivano'));
const BlogPuliziaInterniAutoVapore = lazy(() => import('./pages/BlogPuliziaInterniAutoVapore'));

// CRM Pages (lazy loaded)
const LoginPage = lazy(() => import('./crm/pages/LoginPage'));
const DashboardPage = lazy(() => import('./crm/pages/DashboardPage'));
const LeadsPage = lazy(() => import('./crm/pages/LeadsPage'));
const LeadFormPage = lazy(() => import('./crm/pages/LeadFormPage'));
const LeadDetailPage = lazy(() => import('./crm/pages/LeadDetailPage'));
const ClientsPage = lazy(() => import('./crm/pages/ClientsPage'));
const ClientFormPage = lazy(() => import('./crm/pages/ClientFormPage'));
const ClientDetailPage = lazy(() => import('./crm/pages/ClientDetailPage'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Caricamento...</p>
    </div>
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    // Le pagine sono lazy: attende che l'ancora esista prima di scorrere
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el || ++tries > 40) {
        clearInterval(timer);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96 });
      }
    }, 100);
    return () => clearInterval(timer);
  }, [pathname, hash]);
  
  return null;
};

// Main App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Routes>
      {/* Public Website Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/servizi" element={<PublicLayout><ServiziPage /></PublicLayout>} />
      <Route path="/chi-siamo" element={<PublicLayout><ChiSiamoPage /></PublicLayout>} />
      <Route path="/come-funziona" element={<PublicLayout><ComeFunzionaPage /></PublicLayout>} />
      <Route path="/contatti" element={<PublicLayout><ContattiPage /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/blog/pulire-finestre-tapparelle-senza-aloni" element={<PublicLayout><BlogPulireFinestreSenzaAloni /></PublicLayout>} />
      <Route path="/blog/pulizia-cucina-vapore-guida" element={<PublicLayout><BlogPuliziaCucinaVapore /></PublicLayout>} />
      <Route path="/blog/pulizia-tende-sole-senza-smontaggio" element={<PublicLayout><BlogPuliziaTendeSole /></PublicLayout>} />
      <Route path="/blog/pulizia-vapore-vs-tradizionale" element={<PublicLayout><BlogVaporeVsTradizionale /></PublicLayout>} />
      <Route path="/blog/pulizia-divani-ogni-quanto" element={<PublicLayout><BlogPuliziaDivaniOgniQuanto /></PublicLayout>} />
      <Route path="/blog/acari-materasso-come-eliminarli" element={<PublicLayout><BlogAcariMaterasso /></PublicLayout>} />
      <Route path="/blog/vapore-ecologia-sostenibilita" element={<PublicLayout><BlogVaporeEcologico /></PublicLayout>} />
      <Route path="/blog/pulizia-interni-auto-consigli" element={<PublicLayout><BlogPuliziaInterniAuto /></PublicLayout>} />
      <Route path="/blog/tappeti-persiani-come-pulirli" element={<PublicLayout><BlogTappetiComeRipulirli /></PublicLayout>} />
      <Route path="/blog/costo-pulizia-divano-domicilio-2026" element={<PublicLayout><BlogCostoPuliziaDivano /></PublicLayout>} />
      <Route path="/blog/pulizia-materasso-ogni-quanto-sonno" element={<PublicLayout><BlogPuliziaMaterassoSonno /></PublicLayout>} />
      <Route path="/blog/tappeti-sporchi-cattivi-odori-eliminare" element={<PublicLayout><BlogTappetiSporchiOdori /></PublicLayout>} />
      <Route path="/blog/auto-piena-peli-cane-eliminare-davvero" element={<PublicLayout><BlogAutoPeliCane /></PublicLayout>} />
      <Route path="/blog/7-macchie-difficili-divani-materassi" element={<PublicLayout><BlogMacchieDifficili /></PublicLayout>} />
      <Route path="/blog/pulizia-vapore-casa-bambini" element={<PublicLayout><BlogVaporeCaseBambini /></PublicLayout>} />
      <Route path="/blog/odore-cane-divano-eliminare-definitivamente" element={<PublicLayout><BlogOdoreCaneDivano /></PublicLayout>} />
      <Route path="/blog/pulizia-interni-auto-vapore-vantaggi" element={<PublicLayout><BlogPuliziaInterniAutoVapore /></PublicLayout>} />
      <Route path="/servizi-aziende" element={<PublicLayout><ServiziAziendePage /></PublicLayout>} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
      <Route path="/cookie-policy" element={<PublicLayout><CookiePolicyPage /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
      
      {/* Service Pages - SEO Optimized URLs */}
      <Route path="/lavaggio-divani-bologna" element={<PublicLayout><LavaggioDivaniBologna /></PublicLayout>} />
      <Route path="/lavaggio-materassi-bologna" element={<PublicLayout><LavaggioMaterassiBologna /></PublicLayout>} />
      <Route path="/lavaggio-tappeti-bologna" element={<PublicLayout><LavaggioTappetiBologna /></PublicLayout>} />
      <Route path="/tecnologia-fortador-vapore-lamborghini" element={<PublicLayout><TecnologiaFortador /></PublicLayout>} />
      <Route path="/pulizia-moquette-bologna" element={<PublicLayout><PuliziaMoquetteBologna /></PublicLayout>} />
      <Route path="/pulizia-interni-auto-bologna" element={<PublicLayout><PuliziaInterniAutoBologna /></PublicLayout>} />
      <Route path="/pulizia-camper-bologna" element={<PublicLayout><PuliziaCamperBologna /></PublicLayout>} />
      <Route path="/pulizia-bus-flotte-bologna" element={<PublicLayout><PuliziaBusFlotteBologna /></PublicLayout>} />
      <Route path="/lavaggio-finestre-tapparelle-tende-vapore-bologna" element={<PublicLayout><LavaggioFinestreTapparelleBologna /></PublicLayout>} />
      <Route path="/lavaggio-finestre-aziende-bologna" element={<PublicLayout><LavaggioFinestreAziendeBologna /></PublicLayout>} />
      
      {/* Service Pages - Ferrara */}
      <Route path="/lavaggio-divani-ferrara" element={<PublicLayout><LavaggioDivaniFerrara /></PublicLayout>} />
      <Route path="/lavaggio-materassi-ferrara" element={<PublicLayout><LavaggioMaterassiFerrara /></PublicLayout>} />
      <Route path="/lavaggio-tappeti-ferrara" element={<PublicLayout><LavaggioTappetiFerrara /></PublicLayout>} />
      <Route path="/pulizia-interni-auto-ferrara" element={<PublicLayout><PuliziaInterniAutoFerrara /></PublicLayout>} />
      
      {/* Service Pages - Modena */}
      <Route path="/lavaggio-divani-modena" element={<PublicLayout><LavaggioDivaniModena /></PublicLayout>} />
      <Route path="/lavaggio-materassi-modena" element={<PublicLayout><LavaggioMaterassiModena /></PublicLayout>} />
      <Route path="/lavaggio-tappeti-modena" element={<PublicLayout><LavaggioTappetiModena /></PublicLayout>} />

      {/* 301 lato client per i vecchi URL (il 301 reale è in public/serve.json) */}
      {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate replace to={to} />} />
      ))}
      <Route path="/pulizia-interni-auto-modena" element={<PublicLayout><PuliziaInterniAutoModena /></PublicLayout>} />
      
      {/* Business Service Pages */}
      <Route path="/flotte-aziendali-vapore" element={<PublicLayout><FlotteAziendaliVapore /></PublicLayout>} />
      <Route path="/autobus-trasporto-vapore" element={<PublicLayout><AutobusTrasportoVapore /></PublicLayout>} />
      <Route path="/hotel-bb-pulizia-vapore" element={<PublicLayout><HotelBBPuliziaVapore /></PublicLayout>} />
      <Route path="/ristoranti-locali-vapore" element={<PublicLayout><RistorantiLocaliVapore /></PublicLayout>} />
      
      {/* New Service Pages */}
      <Route path="/pulizia-cucine-bologna" element={<PublicLayout><PuliziaCucineBologna /></PublicLayout>} />
      <Route path="/pulizia-tende-sole-bologna" element={<PublicLayout><PuliziaTendeSoleBologna /></PublicLayout>} />
      <Route path="/pulizia-tende-interne-bologna" element={<PublicLayout><PuliziaTendeInterneBologna /></PublicLayout>} />
      <Route path="/lavaggio-auto-vapore-bologna" element={<PublicLayout><LavaggioAutoVaporeBologna /></PublicLayout>} />
      
      {/* Nuova architettura: vendita, hub e pagine città */}
      {Object.entries(PENDING_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate replace to={to} />} />
      ))}

      {/* CRM Routes */}
      <Route path="/crm/login" element={<LoginPage />} />
      
      {/* Protected CRM Routes */}
      <Route path="/crm" element={
        <ProtectedRoute>
          <CRMLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/new" element={<LeadFormPage />} />
        <Route path="leads/:id" element={<LeadDetailPage />} />
        <Route path="leads/:id/edit" element={<LeadFormPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/new" element={<ClientFormPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="clients/:id/edit" element={<ClientFormPage />} />
      </Route>
      
      {/* Hub, città e vendita (dati in chunk separato) + 404 */}
      <Route path="*" element={<PublicLayout><DataPage /></PublicLayout>} />
    </Routes>
  );
};

// Public Layout wrapper (with Navbar/Footer)
const PublicLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <a href="#main-content" className="skip-link">
      Vai al contenuto principale
    </a>
    <Navbar />
    <main id="main-content" className="flex-grow">
      {children}
    </main>
    <Footer />
    <CookieBanner />
    <WhatsAppButton />
  </div>
);

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <AppRouter />
          </Suspense>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
};

export { AppRouter, PageLoader };
export default App;
